import { Field as ChakraField, Fieldset, RadioGroup, VStack } from '@chakra-ui/react';
import { CockpitFormSeparator } from '@components/cockpit/layouts/CockpitFormLayout';
import { FormInputNumber, FormInputString } from '@components/common/form';
import ModifiedTag from '@components/common/form/ModifiedField';
import { hasChanged } from '@utils/validation/validation.utils';
import { useFormikContext } from 'formik';
import { FormattedMessage } from 'react-intl';

import { GetSensorOrSubSensorConfigurationWithShorthand } from '@/types/sensor/sensorInstallation.types';
import { SensorTypeEnum } from '@/types/sensor/sensors';

type HolodroneFormType = Partial<GetSensorOrSubSensorConfigurationWithShorthand<SensorTypeEnum.HOLODRONE>>;

export function HolodroneConnectivityForm() {
  const { values, initialValues, setValues, setFieldTouched, validateForm } = useFormikContext<HolodroneFormType>();

  return (
    <Fieldset.Root>
      <VStack width="100%" gap={5} alignItems="start">
        <FormInputString label="host" />
        <CockpitFormSeparator />
        <ChakraField.Root variant="cockpit">
          <ChakraField.Label width="100%" height="20px">
            <FormattedMessage id="components.sensorInstallation.form.holodrone.mode" />
            {hasChanged('mode', initialValues, values) && <ModifiedTag />}
          </ChakraField.Label>
          <RadioGroup.Root
            flexDirection="row"
            gap={3}
            width="224px"
            value={values.grpcMode ? 'grpc' : 'tcp'}
            onValueChange={async ({ value }) => {
              if (value === 'grpc') {
                setValues(
                  (prevValues) => ({
                    ...prevValues,
                    grpcMode: true,
                    port: initialValues.port,
                    grpcPort: initialValues.grpcPort ?? initialValues.port,
                  }),
                  false,
                ).then(validateForm);
                setFieldTouched('port', false);
              } else {
                setValues(
                  (prevValues) => ({
                    ...prevValues,
                    grpcMode: false,
                    port: initialValues.port,
                    grpcPort: initialValues.grpcPort,
                    apiKey: initialValues.apiKey,
                  }),
                  false,
                ).then(validateForm);
                setFieldTouched('grpcPort', false);
                setFieldTouched('apiKey', false);
              }
            }}
          >
            <RadioGroup.Item value="grpc">
              <RadioGroup.ItemIndicator />
              <RadioGroup.ItemHiddenInput />
              <RadioGroup.ItemText>
                <FormattedMessage id="components.sensorInstallation.form.holodrone.grpc" />
              </RadioGroup.ItemText>
            </RadioGroup.Item>
            <RadioGroup.Item value="tcp">
              <RadioGroup.ItemIndicator />
              <RadioGroup.ItemHiddenInput />
              <RadioGroup.ItemText>
                <FormattedMessage id="components.sensorInstallation.form.holodrone.tcp" />
              </RadioGroup.ItemText>
            </RadioGroup.Item>
          </RadioGroup.Root>
        </ChakraField.Root>
        {values.grpcMode ? (
          <>
            <FormInputString label="apiKey" i18nKey="components.sensorInstallation.form.holodrone.apiKey" />
            <FormInputNumber
              label="grpcPort"
              i18nKey="components.sensorInstallation.form.holodrone.grpcPort"
              max={65535}
            />
          </>
        ) : (
          <FormInputNumber label="port" max={65535} />
        )}
        <CockpitFormSeparator />
        <FormInputNumber label="timeoutThreshold" suffix="ms" width="150px" />
      </VStack>
    </Fieldset.Root>
  );
}
